import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProducts, deleteProduct, getProductImageUrl } from "@/services/firebase/product";
import { Product } from "@/types/product";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ActionMenu from "@/components/common/ActionMenu";
import { useDebounce } from "@/hooks/useDebounce";
import { Image as ImageIcon } from "lucide-react";
import {
  AdminPage,
  AdminPageHeader,
  AdminSearchInput,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminDataTable,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

// 商品圖片組件 — 同步 build URL,瀏覽器自動 disk-cache
function ProductImage({ product }: { product: Product }) {
  const imageUrl = getProductImageUrl(product.main_image);

  if (!imageUrl) {
    return (
      <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
        <ImageIcon className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={product.name}
      width={64}
      height={64}
      loading="lazy"
      decoding="async"
      className="w-16 h-16 object-cover rounded-lg bg-muted"
    />
  );
}

const columns: AdminTableColumn[] = [
  { key: "image", label: "圖片" },
  { key: "name", label: "商品名稱" },
  { key: "price", label: "價格" },
  { key: "status", label: "狀態" },
  { key: "stock", label: "庫存", hideBelow: "md" },
  { key: "category", label: "分類", hideBelow: "md" },
  { key: "created_at", label: "建立時間", hideBelow: "lg" },
  { key: "actions", label: "操作" },
];

function getCategoryLabel(category?: string) {
  switch (category) {
    case "merchandise": return "周邊商品";
    case "clothing": return "服飾配件";
    case "collectibles": return "收藏品";
    case "digital": return "數位商品";
    case "other": return "其他";
    default: return "-";
  }
}

export default function ProductListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string | null;
    name: string;
  }>({ isOpen: false, id: null, name: "" });

  const { data: products, isLoading, error } = useQuery({
    queryKey: ["products"],
    queryFn: () => getProducts(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
      setDeleteError(null);
    },
    onError: (error) => {
      console.error("刪除商品失敗:", error);
      setDeleteError(error instanceof Error ? error.message : "未知錯誤");
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
    },
  });

  // 過濾和搜尋
  const filteredProducts = useMemo(() => {
    if (!products || products.length === 0) return [];

    return products.filter((product) => {
      const searchLower = debouncedSearchTerm.toLowerCase();
      const matchesSearch =
        !searchLower ||
        product.id.toLowerCase().includes(searchLower) ||
        (product.name && product.name.toLowerCase().includes(searchLower)) ||
        (product.description && product.description.toLowerCase().includes(searchLower));

      const matchesStatus = statusFilter === "all" || product.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [products, debouncedSearchTerm, statusFilter]);

  const handleDelete = (id: string, name: string) => {
    setDeleteConfirm({ isOpen: true, id, name });
  };

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="商品管理" createHref="/products/new" createLabel="新增商品" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="商品管理" createHref="/products/new" createLabel="新增商品" />
        <AdminErrorAlert
          message={`載入商品時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  const renderCell = (product: Product, column: AdminTableColumn) => {
    switch (column.key) {
      case "image":
        return <ProductImage product={product} />;
      case "name":
        return (
          <div className="text-sm font-medium text-foreground">
            {product.name || "-"}
          </div>
        );
      case "price":
        return (
          <span className="text-sm text-muted-foreground">
            ${product.price?.toLocaleString() || "0"}
          </span>
        );
      case "status":
        return (
          <Badge variant={product.status === "active" ? "default" : "destructive"}>
            {product.status === "active" ? "上架中" : "已下架"}
          </Badge>
        );
      case "stock":
        return (
          <span className="text-sm text-muted-foreground">
            {product.stock_quantity !== undefined ? product.stock_quantity : "-"}
          </span>
        );
      case "category":
        return (
          <span className="text-sm text-muted-foreground">
            {getCategoryLabel(product.category)}
          </span>
        );
      case "created_at":
        return (
          <span className="text-sm text-muted-foreground">
            {product.created_at
              ? new Date(product.created_at).toLocaleString("zh-TW")
              : "-"}
          </span>
        );
      case "actions":
        return (
          <ActionMenu
            actions={[
              {
                label: "編輯",
                onClick: () => navigate(`/products/${product.id}/edit`),
              },
              {
                label: "刪除",
                onClick: () => handleDelete(product.id, product.name || product.id),
                disabled: deleteMutation.isPending,
                variant: "danger",
              },
            ]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AdminPage>
      {deleteError && (
        <AdminErrorAlert message={deleteError!} onDismiss={() => setDeleteError(null)} />
      )}

      <AdminPageHeader
        title="商品管理"
        createHref="/products/new"
        createLabel="新增商品"
        actions={
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | "active" | "inactive")}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="狀態" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="active">上架中</SelectItem>
              <SelectItem value="inactive">已下架</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <AdminSearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="搜尋商品名稱或描述..."
      />

      <div className="mt-4">
        <AdminDataTable
          columns={columns}
          data={filteredProducts}
          renderCell={renderCell}
          getRowId={(item) => item.id}
          emptyTitle={
            searchTerm || statusFilter !== "all"
              ? "沒有符合條件的商品"
              : "尚無商品資料"
          }
          emptyActionHref={!searchTerm && statusFilter === "all" ? "/products/new" : undefined}
          emptyActionLabel="新增第一個商品"
        />
      </div>

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除商品「${deleteConfirm.name}」嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: "" })}
      />
    </AdminPage>
  );
}
